/**
 * Automations API using Firebase services
 */
import {
    createAutomationDraft,
    updateAutomationDraft,
    getAutomation,
    listAutomations,
    activateAutomation as activateCallable,
    deactivateAutomation as deactivateCallable,
    stopAutomation as stopCallable,
    runAutomationTest as runTestCallable,
    deleteAutomation as deleteCallable,
} from '../../../firebase/automations.service';

export const fetchAutomations = async () => {
    return await listAutomations();
};

export const fetchAutomation = async (id) => {
    return await getAutomation(id);
};

export const createAutomation = async (payload) => {
    const id = await createAutomationDraft(payload);
    return { id, ...payload };
};

export const updateAutomation = async (id, payload) => {
    await updateAutomationDraft(id, payload);
    return { id, ...payload };
};

export const activateAutomation = async (id) => {
    const result = await activateCallable(id);
    return {
        id,
        status: 'ACTIVE',
        executionsCreated: result?.executionsCreated || 0,
        executionIds: result?.executionIds || [],
        executionsResumed: result?.executionsResumed || 0,
        resumedExecutionIds: result?.resumedExecutionIds || [],
    };
};

export const deactivateAutomation = async (id) => {
    await deactivateCallable(id);
    return { id, status: 'PAUSED' };
};

/**
 * Stop automation - Hard stop that terminates all executions
 * @param {string} id - Automation ID
 * @returns {Promise<Object>} - Result { id, status, executionsStopped, executionIds }
 */
export const stopAutomation = async (id) => {
    const result = await stopCallable(id);
    return {
        id,
        status: 'STOPPED',
        executionsPaused: result?.executionsPaused || 0,
        executionIds: result?.executionIds || [],
    };
};

/**
 * Run automation test - Ephemeral execution with test contact
 * @param {string} id - Automation ID  
 * @param {Object} testContact - { testEmail, testPhone }
 * @returns {Promise<Object>} - Step-by-step execution summary
 */
export const runAutomationTest = async (id, testContact) => {
    const result = await runTestCallable(id, testContact);
    return result;
};

/**
 * Toggle automation status between ACTIVE and PAUSED
 * @param {string} id - Automation ID
 * @param {boolean} isActive - Whether to activate (true) or deactivate (false)
 * @returns {Promise<{id: string, status: string}>}
 */
export const toggleAutomationStatus = async (id, isActive) => {
    if (isActive) {
        return await activateAutomation(id);
    } else {
        return await deactivateAutomation(id);
    }
};

/**
 * Delete automation and all related data - Cascading delete
 * @param {string} id - Automation ID
 * @returns {Promise<Object>} - Result { automationId, executionsDeleted, tasksDeleted }
 */
export const deleteAutomationById = async (id) => {
    const result = await deleteCallable(id);
    return result;
};

export default {
    fetchAutomations,
    fetchAutomation,
    createAutomation,
    updateAutomation,
    activateAutomation,
    deactivateAutomation,
    stopAutomation,
    runAutomationTest,
    toggleAutomationStatus,
    deleteAutomationById,
};
